import { useCallback, useState } from 'react';
import { updateQmsCategoryStatusApiService } from '../api/updateQmsCategoryStatusApiService';
import { QMS_CATEGORY_LABELS } from '../constants/labels';

export interface QmsCategoryStatusTarget {
  id: string;
  name: string;
  status: 'active' | 'inactive';
}

interface UseUpdateQmsCategoryStatusOptions {
  onSuccess: () => void;
}

export const useUpdateQmsCategoryStatus = ({
  onSuccess,
}: UseUpdateQmsCategoryStatusOptions) => {
  const [selectedCategory, setSelectedCategory] =
    useState<QmsCategoryStatusTarget | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [showSuccessSnackbar, setShowSuccessSnackbar] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showErrorSnackbar, setShowErrorSnackbar] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handleOpenDialog = useCallback((category: QmsCategoryStatusTarget) => {
    setSelectedCategory(category);
    setIsDialogOpen(true);
  }, []);

  const handleDialogClose = useCallback(() => {
    if (!isSubmitting) {
      setIsDialogOpen(false);
      setSelectedCategory(null);
    }
  }, [isSubmitting]);

  const handleConfirm = useCallback(async () => {
    if (!selectedCategory) {
      return;
    }

    setIsSubmitting(true);
    const isActive = selectedCategory.status !== 'active';

    try {
      const result = await updateQmsCategoryStatusApiService.updateStatus({
        categoryId: selectedCategory.id,
        isActive,
      });

      if (result.success) {
        setSuccessMessage(
          isActive
            ? QMS_CATEGORY_LABELS.STATUS_SUCCESS_ACTIVATED
            : QMS_CATEGORY_LABELS.STATUS_SUCCESS_DEACTIVATED
        );
        setShowSuccessSnackbar(true);
        setIsDialogOpen(false);
        setSelectedCategory(null);
        onSuccess();
      } else {
        setErrorMessage(
          result.error ?? QMS_CATEGORY_LABELS.STATUS_ERROR_GENERIC
        );
        setShowErrorSnackbar(true);
        setIsDialogOpen(false);
        setSelectedCategory(null);
        if (result.alreadyInStatus) {
          onSuccess();
        }
      }
    } catch {
      setErrorMessage(QMS_CATEGORY_LABELS.STATUS_ERROR_UNEXPECTED);
      setShowErrorSnackbar(true);
      setIsDialogOpen(false);
      setSelectedCategory(null);
    } finally {
      setIsSubmitting(false);
    }
  }, [selectedCategory, onSuccess]);

  return {
    selectedCategory,
    isDialogOpen,
    isSubmitting,
    handleOpenDialog,
    handleDialogClose,
    handleConfirm,
    showSuccessSnackbar,
    successMessage,
    setShowSuccessSnackbar,
    showErrorSnackbar,
    errorMessage,
    setShowErrorSnackbar,
  };
};
